import { useEffect, useMemo, useState } from "react"

import { capitalize } from "lodash"
import { XIcon } from "lucide-react"
import { DefaultPopover, Input, Select, InputGroup, Separator } from "twyne-new.ui"

import { HIDE_SIZES, PREDEFINED_SIZES, UNIT_OPTIONS } from "../../utils/predefined_sizes"

import { useFrame } from "./context"

const Popover = ({ size = false, children, onChange, onEditing, position, type, config, config: { value, unit, predefined } }) => {
  const [open, setOpen] = useState(false)
  const { setFrame, frame: { sizeTab } } = useFrame()

  const handleClosePopover = value => {
    onEditing && onEditing(value, position, type)
    size && setFrame(prev => ({ ...prev, sizeTab: 'standard', standard: config }))
    setOpen(!open)
  }

  const predefinedOptions = useMemo(() => PREDEFINED_SIZES[type]
    .filter(opt => !HIDE_SIZES[size ? type : 'space']?.[sizeTab]
      ?.includes(opt.value)), [size, type, sizeTab])

  const handleChange = ({ unit: propsUnit = unit, value: propsValue = value, predefined: propsPredefined = predefined }) => {
    onChange({ value: propsValue, unit: propsUnit, predefined: propsPredefined })
  }

  return <>
    <DefaultPopover
      trigger={children}
      triggerProps={{ asChild: true }}
      contentProps={{ side: position || 'top' }}
      wrapperProps={{ open, onOpenChange: handleClosePopover }}
    >
      <div className='flex justify-between items-center'>
        {capitalize(type)}
        <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <Separator className='m-0 my-2' />
      <div className='flex flex-col gap-2'>
        <SizeInputs
          value={value}
          unit={unit}
          onChange={handleChange}
          predefinedOptions={predefinedOptions}
          open={open}
          type={type}
        />
      </div>
    </DefaultPopover>
  </>
}

const SizeInputs = ({ value, unit, onChange, predefinedOptions, type }) => {
  const [arbitrary, setArbitrary] = useState()

  const handleReset = () => {
    setArbitrary(true)
    onChange({ value: null, predefined: false, unit: 'px' })
  }

  const handleChangePredefinedSelect = option => {
    if (!option) {
      return handleReset()
    }

    if (option?.value === 'screen' && type === 'height') {
      setArbitrary(true)
      return onChange({ value: '100', unit: 'vh', predefined: false })
    }

    const isArbitrary = option?.value === 'arbitrary'
    setArbitrary(isArbitrary)
    const newValue = isArbitrary ? '' : option?.value
    onChange({ value: newValue, predefined: !isArbitrary, unit: isArbitrary ? 'px' : null })
  }

  useEffect(() => {
    if (arbitrary === undefined) {
      setArbitrary(unit ? true : value ? false : true)
    }
  }, [value, unit, arbitrary])

  return <>
    <div className='flex flex-col gap-1'>
      <Select
        value={arbitrary ? 'arbitrary' : value}
        options={[{ label: 'Arbitrary', value: 'arbitrary' }, ...predefinedOptions]}
        onChange={handleChangePredefinedSelect}
        clearable
        variant='accent'
        creatable
        isValidNewOption={value => value && !isNaN(value) || value.includes('/')}
        promptTextCreator={str => `Create an new option '${str}'`}
        placeholder='Type to create a size'
      />

      {arbitrary && <>
        <InputGroup variant='accent'>
          <Input
            variant='accent'
            type='number'
            size='sm'
            onChange={e => onChange({ value: e.target.value, predefined: false })}
            value={value || ''}
          />

          <Select
            value={unit}
            options={UNIT_OPTIONS}
            onChange={option => onChange({ unit: option.value })}
            variant='accent'
          />
        </InputGroup>
      </>}

    </div>
  </>
}

export default Popover
